import {
  ENTRY_DATA_SECTION_KEYS as SECTION_KEYS,
  ENTRY_DATA_KEY_COMMENTS_GROUP_TABLE_KEY as KEY_COMMENT,
  ENTRY_DATA_KEY_COMMENTS_SHELL_KEYS as KEY_COMMENT_SHELL_KEYS,
} from "@frontend/common";

import { FundingFilingGroupTable } from "@/types/ventureCapital.types";
import {
  useTableCreatorFocusStatus,
  DEFAULT_FOCUS_STATUS,
} from "@FillInTableContainer/shared/context/TableCreatorFocusStatusContext";
import { useTableCreatorCurrentHistory } from "@FillInTableContainer/shared/context/TableCreatorHistoryContext";
import {
  useTableCreatorPositionInfo,
  DEFAULT_POSITION_INFO,
} from "@FillInTableContainer/shared/context/TableCreatorPositionInfoContext";
import { useEntryDataState } from "@stores/entryData";
import { getPressIdFromURL } from "@utils/getStateFromURL";

import type { ArticleEntryData } from "@/types/articleEntryData.types";

interface UseHandleGroupShellReturn {
  handleClickToClearKeyCommentGroupShell: () => void;
  handleKeyDownToClearKeyCommentGroupShell: (event: KeyboardEvent) => void;
}

interface Props {
  positionPropsInfo: {
    groupTableKey: FundingFilingGroupTable;
    groupTableIndex: number;
    groupShellKey: string | null;
    groupShellIndex: number | null;
    tableKey: string | null;
    tableIndex: number;
  };
}

const articleSection = SECTION_KEYS.fundingFiling;
export const useHandleVentureCapitalKeyCommentGroupShell = ({
  positionPropsInfo,
}: Props): UseHandleGroupShellReturn => {
  const {
    groupTableKey,
    groupTableIndex,
    groupShellKey,
    groupShellIndex,
    tableKey,
    tableIndex,
  } = positionPropsInfo;

  const [entryData, setEntryData] = useEntryDataState(getPressIdFromURL());
  const { changeCurrentHistory } = useTableCreatorCurrentHistory();
  const { focusStatus, changeFocusStatus } = useTableCreatorFocusStatus();
  const { info, changeInfo } = useTableCreatorPositionInfo();

  const handleClearGroupShell = () => {
    const clearKeyCommentGroupShell = (): ArticleEntryData => {
      return {
        ...entryData,
        [articleSection]: {
          ...entryData[articleSection],
          [KEY_COMMENT]: {
            ...entryData[articleSection]?.[KEY_COMMENT],
            [KEY_COMMENT_SHELL_KEYS.quote]: {
              ...entryData[articleSection]?.[KEY_COMMENT]?.[
                KEY_COMMENT_SHELL_KEYS.quote
              ],
              value: [],
            },

            [KEY_COMMENT_SHELL_KEYS.spokesperson]: {
              ...entryData[articleSection]?.[KEY_COMMENT]?.[
                KEY_COMMENT_SHELL_KEYS.spokesperson
              ],
              value: [],
            },
          },
        },
      } as ArticleEntryData;
    };

    if (info.groupTableKey === KEY_COMMENT) {
      setEntryData(clearKeyCommentGroupShell());
      changeCurrentHistory({
        entryData: clearKeyCommentGroupShell(),
        changedDataInfo: {
          target: "groupShell",
          action: "clear",
          position: {
            groupShellKey,
            groupTableIndex,
            tableKey,
            tableIndex,
            groupTableKey,
            groupShellIndex,
            shellKey: null,
            shellIndex: null,
            valueIndex: null,
            value: null,
          },
        },
      });
      changeFocusStatus(DEFAULT_FOCUS_STATUS);
      changeInfo(DEFAULT_POSITION_INFO);
    }
  };

  const handleClickToClearKeyCommentGroupShell = () => {
    if (focusStatus.type === "clear") {
      handleClearGroupShell();
    }
  };

  const handleKeyDownToClearKeyCommentGroupShell = (event: KeyboardEvent) => {
    if (focusStatus.type === "clear" && event.key === "Delete") {
      handleClearGroupShell();
    }
  };

  return {
    handleClickToClearKeyCommentGroupShell,
    handleKeyDownToClearKeyCommentGroupShell,
  };
};
